import { mockShell } from "./shell.mock"
import type { JoowShell, ShellLocale, ShellTheme } from "./shell"

const appId = "joowfak"
const defaultSurface = "user"
const bridgeVersion = 4
const runtimeKey = "joowfak.shellRuntime"

export interface BridgeRuntime {
  appId: string
  surface: string
  token: string
  apiBasePath: string
  platformApiBasePath: string
  realtimeApiBaseUrl?: string
  sessionHandle?: string
  locale: ShellLocale
  theme: ShellTheme
  subjectId: string
  displayName?: string
  avatarUrl?: string
  permissions: string[]
}

type LaunchMessage = Record<string, unknown>

let runtime: BridgeRuntime | null = readStoredRuntime()
let runtimePromise: Promise<BridgeRuntime | null> | null = null
const themeListeners = new Set<(theme: ShellTheme) => void>()
const localeListeners = new Set<(locale: ShellLocale) => void>()

export function currentBridgeRuntime(): BridgeRuntime | null {
  return runtime
}

export function bridgeApiBasePath(): string | null {
  return runtime?.apiBasePath || null
}

export function bridgeAuthorizationHeader(): string | null {
  return runtime?.token ? `Bearer ${runtime.token}` : null
}

export function bridgeSessionHandle(): string | null {
  return runtime?.sessionHandle || null
}

export function isBridgeEmbeddedCandidate(): boolean {
  if (typeof window === "undefined") return false
  if (window.parent && window.parent !== window) return true
  const params = new URLSearchParams(window.location.search)
  return params.has("joow_app") || params.has("joow_surface") || params.has("joow_exchange_path")
}

export async function ensureBridgeRuntime(timeoutMs = 3500): Promise<BridgeRuntime | null> {
  if (runtime) return runtime
  if (!isBridgeEmbeddedCandidate()) return null
  if (!runtimePromise) {
    runtimePromise = waitForLaunch(timeoutMs)
      .then(exchangeLaunch)
      .then((nextRuntime) => {
        setRuntime(nextRuntime)
        return nextRuntime
      })
      .catch(() => null)
      .finally(() => {
        runtimePromise = null
      })
  }
  return runtimePromise
}

export function clearBridgeRuntime(): void {
  runtime = null
  try {
    sessionStorage.removeItem(runtimeKey)
  } catch {
    // Storage is optional in embedded WebViews.
  }
}

export function notifyBridgeAuthExpired(): void {
  clearBridgeRuntime()
  try {
    window.parent?.postMessage({ type: "joow:auth-expired", app: appId, appId }, "*")
  } catch {
    // Parent notification is best effort.
  }
}

export async function getRealtimeToken(input: { resourceType: string; resourceId: string; audience?: string; ttlSeconds?: number }): Promise<{ accessToken: string; realtimeApiBaseUrl?: string }> {
  const active = await ensureBridgeRuntime()
  if (!active) throw new Error("Joow shell session is required for realtime")
  const endpoint = joinPlatformPath(active.platformApiBasePath, `/joow/app-sessions/${encodeURIComponent(active.appId)}/realtime-token`)
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${active.token}`,
    },
    body: JSON.stringify({
      audience: input.audience || "realtime.joowcore.com",
      resourceType: input.resourceType,
      resourceId: input.resourceId,
      ttlSeconds: input.ttlSeconds,
    }),
  })
  if (response.status === 401 || response.status === 403) notifyBridgeAuthExpired()
  if (!response.ok) throw new Error(`Realtime token request failed: ${response.status}`)
  const payload = await response.json()
  const data = objectValue(payload.data) || objectValue(payload) || {}
  const accessToken = stringValue(data.accessToken) || stringValue(data.access_token)
  if (!accessToken) throw new Error("Realtime token response did not include accessToken")
  return {
    accessToken,
    realtimeApiBaseUrl: stringValue(data.realtimeApiBaseUrl) || stringValue(data.realtime_api_base_url) || active.realtimeApiBaseUrl,
  }
}

export function createBridgeShell(): JoowShell | null {
  if (!isBridgeEmbeddedCandidate() && !runtime) return null
  return {
    ...mockShell,
    identity: {
      async getPrincipal() {
        const active = await ensureBridgeRuntime()
        return {
          principalToken: active?.subjectId || "embedded-user",
          region: "IR",
          locale: active?.locale || "fa-IR",
        }
      },
      async isAuthenticated() {
        return Boolean(await ensureBridgeRuntime())
      },
      async requireAuth() {
        const active = await ensureBridgeRuntime()
        if (!active) throw new Error("Joow shell authentication is required")
      },
    },
    surface: {
      ...mockShell.surface,
      theme() {
        return runtime?.theme || initialTheme()
      },
      locale() {
        return runtime?.locale || initialLocale()
      },
      onThemeChange(cb) {
        themeListeners.add(cb)
        return () => themeListeners.delete(cb)
      },
      onLocaleChange(cb) {
        localeListeners.add(cb)
        return () => localeListeners.delete(cb)
      },
    },
    version: `bridge-${bridgeVersion}`,
  }
}

function waitForLaunch(timeoutMs: number): Promise<LaunchMessage> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener("message", listener)
      reject(new Error("Timed out waiting for Joow launch"))
    }, timeoutMs)
    const listener = (event: MessageEvent) => {
      const data = objectValue(event.data)
      if (!data || data.type !== "joow:launch") return
      if (stringValue(data.appId) !== appId && stringValue(data.app) !== appId) return
      window.clearTimeout(timeout)
      window.removeEventListener("message", listener)
      resolve(data)
    }
    window.addEventListener("message", listener)
    window.parent?.postMessage({
      type: "joow:ready",
      app: appId,
      appId,
      surface: defaultSurface,
      bridgeVersion,
    }, "*")
  })
}

async function exchangeLaunch(launch: LaunchMessage): Promise<BridgeRuntime> {
  const launchCode = stringValue(launch.launchCode) || stringValue(launch.code)
  if (!launchCode) throw new Error("Joow launch did not include a launch code")
  const surface = stringValue(launch.surface) || stringValue(launch.surfaceId) || defaultSurface
  const platformApiBasePath = stringValue(launch.platformApiBaseUrl) || stringValue(launch.platformApiBasePath) || queryParam("joow_platform_api_base") || "/api"
  const response = await fetch(stringValue(launch.exchangePath) || joinPlatformPath(platformApiBasePath, "/joow/app-sessions/exchange"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      launchCode,
      code: launchCode,
      app: appId,
      appId,
      surface,
      origin: window.location.origin,
      path: `${window.location.pathname}${window.location.search}`,
      bridgeVersion,
    }),
  })
  if (!response.ok) throw new Error(`Joow app-session exchange failed: ${response.status}`)
  const payload = objectValue(await response.json()) || {}
  const context = objectValue(payload.context) || {}
  const user = objectValue(payload.user) || {}
  return normalizeRuntime({
    appId: stringValue(payload.appId) || stringValue(payload.app) || appId,
    surface: stringValue(payload.surface) || surface,
    token: stringValue(payload.token) || stringValue(payload.accessToken) || stringValue(payload.appSessionToken) || "",
    apiBasePath: stringValue(payload.appApiBaseUrl) || stringValue(payload.appApiBasePath) || stringValue(payload.apiBaseUrl) || stringValue(payload.apiBasePath) || stringValue(launch.appApiBaseUrl) || stringValue(launch.apiBaseUrl) || `/api/joow/apps/${appId}`,
    platformApiBasePath: stringValue(payload.platformApiBaseUrl) || stringValue(payload.platformApiBasePath) || platformApiBasePath,
    realtimeApiBaseUrl: stringValue(payload.realtimeApiBaseUrl) || stringValue(payload.realtime_api_base_url) || stringValue(launch.realtimeApiBaseUrl),
    sessionHandle: stringValue(payload.sessionHandle) || stringValue(payload.appSessionHandle),
    locale: normalizeLocale(stringValue(payload.locale) || stringValue(context.locale) || stringValue(launch.locale) || stringValue(launch.language)),
    theme: normalizeTheme(stringValue(payload.themeMode) || stringValue(payload.colorScheme) || stringValue(context.themeMode) || stringValue(context.colorScheme) || stringValue(launch.themeMode) || stringValue(launch.colorScheme)),
    subjectId: stringValue(context.subjectId) || stringValue(context.centralSubjectId) || stringValue(context.userId) || stringValue(user.subjectId) || stringValue(user.id) || "",
    displayName: stringValue(context.displayName) || stringValue(user.displayName) || stringValue(user.name),
    avatarUrl: stringValue(context.avatarUrl) || stringValue(user.avatarUrl),
    permissions: stringArray(context.permissions || context.capabilities || payload.permissions || payload.surfaceCapabilities || payload.capabilities),
  })
}

function setRuntime(nextRuntime: BridgeRuntime): void {
  const previous = runtime
  runtime = nextRuntime
  try {
    sessionStorage.setItem(runtimeKey, JSON.stringify(nextRuntime))
  } catch {
    // Storage is optional in embedded WebViews.
  }
  if (!previous || previous.theme !== nextRuntime.theme) {
    themeListeners.forEach((listener) => listener(nextRuntime.theme))
  }
  if (!previous || previous.locale !== nextRuntime.locale) {
    localeListeners.forEach((listener) => listener(nextRuntime.locale))
  }
}

function readStoredRuntime(): BridgeRuntime | null {
  try {
    const raw = sessionStorage.getItem(runtimeKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return normalizeRuntime(parsed)
  } catch {
    return null
  }
}

function normalizeRuntime(input: Partial<BridgeRuntime>): BridgeRuntime {
  return {
    appId: stringValue(input.appId) || appId,
    surface: stringValue(input.surface) || defaultSurface,
    token: stringValue(input.token) || "",
    apiBasePath: stringValue(input.apiBasePath) || `/api/joow/apps/${appId}`,
    platformApiBasePath: stringValue(input.platformApiBasePath) || "/api",
    realtimeApiBaseUrl: stringValue(input.realtimeApiBaseUrl),
    sessionHandle: stringValue(input.sessionHandle),
    locale: normalizeLocale(input.locale),
    theme: normalizeTheme(input.theme),
    subjectId: stringValue(input.subjectId) || "",
    displayName: stringValue(input.displayName),
    avatarUrl: stringValue(input.avatarUrl),
    permissions: stringArray(input.permissions),
  }
}

function joinPlatformPath(basePath: string, path: string): string {
  const base = basePath.replace(/\/$/, "")
  if (!base) return `/api${path}`
  if (base.endsWith("/api")) return `${base}${path}`
  return `${base}/api${path}`
}

function queryParam(key: string): string {
  return new URLSearchParams(window.location.search).get(key) || ""
}

function initialLocale(): ShellLocale {
  const value = queryParam("joow_locale") || queryParam("locale")
  return normalizeLocale(value)
}

function initialTheme(): ShellTheme {
  const value = queryParam("joow_theme") || queryParam("theme")
  return normalizeTheme(value)
}

function normalizeLocale(value: unknown): ShellLocale {
  if (value === "en" || value === "en-US" || value === "en_US") return "en"
  return "fa-IR"
}

function normalizeTheme(value: unknown): ShellTheme {
  if (value === "dark" || value === "midnight") return "midnight"
  return "daylight"
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? value as Record<string, unknown> : null
}

function stringValue(value: unknown): string {
  return typeof value === "string" && value.trim() ? value.trim() : ""
}

function stringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => stringValue(item)).filter(Boolean)
  if (typeof value === "string") return value.split(",").map((item) => item.trim()).filter(Boolean)
  return []
}
